/**
 * Policy Acceptance Checkbox Component
 *
 * Mandatory checkbox for policy acceptance.
 * Must be checked before proceeding with subscription or trading enable.
 *
 * Records acceptance for all three policies (Terms, Privacy, and Risk)
 * so that a timestamped record exists for each legal agreement.
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { acceptPolicy, getPolicyStatus, type PolicyStatusResponse } from "@app/services/policyApi";
import "./policy-acceptance.css";

const POLICY_STATUS_QUERY_KEY = ["user", "policy", "status"];

// Policy types that must be accepted together
const POLICY_TYPES = ["terms", "privacy", "risk"] as const;
type PolicyType = (typeof POLICY_TYPES)[number];

interface PolicyAcceptanceCheckboxProps {
  onAccepted?: () => void;
  onRejected?: () => void;
  required?: boolean;
  showStatus?: boolean;
}

export function PolicyAcceptanceCheckbox({
  onAccepted,
  onRejected,
  required = true,
  showStatus = false,
}: PolicyAcceptanceCheckboxProps): JSX.Element {
  const queryClient = useQueryClient();
  const [isAccepted, setIsAccepted] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  const { data: policyStatus } = useQuery<PolicyStatusResponse>({
    queryKey: POLICY_STATUS_QUERY_KEY,
    queryFn: getPolicyStatus,
  });

  // Check whether all required policies have been accepted
  const hasAcceptedAll = POLICY_TYPES.every(
    (policyType) => policyStatus?.policies?.[policyType]?.accepted ?? false
  );

  const acceptMutation = useMutation({
    mutationFn: async () => {
      // Accept all three policies so each has a timestamped record
      await Promise.all(
        POLICY_TYPES.map((policyType) => acceptPolicy({ policy_type: policyType }))
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: POLICY_STATUS_QUERY_KEY });
      setIsAccepted(true);
      if (onAccepted) {
        onAccepted();
      }
    },
    onError: () => {
      setIsAccepted(false);
      if (onRejected) {
        onRejected();
      }
    },
  });

  useEffect(() => {
    if (hasAcceptedAll) {
      setIsAccepted(true);
      setHasChecked(true);
    }
  }, [hasAcceptedAll]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setHasChecked(true);

    if (checked) {
      if (!hasAcceptedAll) {
        acceptMutation.mutate();
      } else {
        setIsAccepted(true);
        if (onAccepted) {
          onAccepted();
        }
      }
    } else {
      setIsAccepted(false);
      if (onRejected) {
        onRejected();
      }
    }
  };

  const isDisabled = acceptMutation.isPending;

  return (
    <div className="policy-acceptance">
      <label className="policy-acceptance__label">
        <input
          type="checkbox"
          checked={isAccepted}
          onChange={handleCheckboxChange}
          disabled={isDisabled}
          required={required}
          className="policy-acceptance__checkbox"
        />
        <span className="policy-acceptance__text">
          I agree to the{" "}
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
          {", "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          {", and "}
          <a href="/risk-disclaimer" target="_blank" rel="noopener noreferrer">
            Risk Disclaimer
          </a>
          {required && " (required)"}
        </span>
      </label>

      {acceptMutation.isError && (
        <div className="policy-acceptance__error">
          Failed to record policy acceptance. Please try again.
        </div>
      )}

      {showStatus && policyStatus && (
        <div className="policy-acceptance__status">
          {hasAcceptedAll ? (
            <span className="policy-acceptance__status--accepted">
              ✓ Terms, Privacy Policy, and Risk Disclaimer accepted
            </span>
          ) : (
            <span className="policy-acceptance__status--pending">
              Policy acceptance required
            </span>
          )}
        </div>
      )}
    </div>
  );
}
